/**
 * C2 — "IP arrangements".
 *
 * An IP arrangement is an option, a licence or an assignment an organisation
 * proposes on a project the inventor has granted it. Acknowledging it puts it
 * on the record; it transfers no ownership on InnovateFlow, it does not replace
 * the signed agreement, and — this is the point — IT GRANTS NOTHING. What an
 * organisation can SEE is decided in the grid above, by the inventor, and an
 * arrangement never changes that. The route behind these buttons cannot write a
 * permission at all (lambda/innovateflow-api/ip_arrangements/handler.py).
 *
 * The confirmation is not decoration. "I understand" already means something
 * very different on this platform — it lifts folder exclusions for a portfolio
 * — so acknowledging asks for its own sentence, in its own words, and the same
 * sentence is shown to the organisation beside Propose. Both sides read one
 * definition of what they are doing.
 *
 * On a co-owned invention EVERY owner must acknowledge before the record reads
 * as binding, and any ONE owner may decline or dispute, which ends it for
 * everyone. A refusal needs one voice; a binding needs all of them. The card
 * says so in the row ("you and 2 other owners must each acknowledge") and in
 * the note underneath.
 *
 * The rows are real: the page reads GET /ip-arrangements/mine and hands them
 * down. When there are none, the empty state says there are none — never an
 * invented example, on a screen about who can see your work.
 */
import { useState } from 'react'
import { dayAndMonth } from './sharingModel'
import {
  ACKNOWLEDGE_CONFIRMATION, LIVE_STATUSES, kindLabel, ownersSentence, statusSentence,
} from './ipModel'
import type { IpArrangement } from '../../types/ipArrangements'

export interface IpArrangementsProps {
  arrangements: IpArrangement[]
  /** false only while a real read is in flight. */
  loaded: boolean
  /** A real read failure — never confused with "nothing proposed". */
  error?: boolean
  onAcknowledge?: (arrangementId: string) => Promise<void>
  onDecline?: (arrangementId: string) => Promise<void>
  onDispute?: (arrangementId: string) => Promise<void>
}

export default function IpArrangements({
  arrangements, loaded, error = false, onAcknowledge, onDecline, onDispute,
}: IpArrangementsProps) {
  // Which row is showing the confirmation sentence. Only one at a time: this is
  // a deliberate act, not a checkbox, and two half-open confirmations on one
  // card is how somebody acknowledges the wrong invention.
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const run = async (id: string, fn?: (id: string) => Promise<void>) => {
    if (!fn) return
    setBusy(id)
    setFailed(null)
    try {
      await fn(id)
      setConfirming(null)
    } catch {
      // A refused answer must SAY it was refused. Leaving the row looking
      // answered would be the worst outcome on a legal record.
      setFailed(id)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      id="ip-arrangements"
      data-part="IpArrangements"
      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
    >
      <h2 className="mb-1 flex items-center gap-2 text-[14px] font-bold text-slate-900">
        IP arrangements
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
          {loaded && !error ? arrangements.length : '—'}
        </span>
      </h2>

      {!loaded && !error && <p className="text-[12.5px] text-slate-400">Loading…</p>}

      {error && (
        <p className="text-[12.5px] leading-relaxed text-rose-600">
          Could not load your IP arrangements. This is not a statement that there
          are none — reload to see where they stand.
        </p>
      )}

      {loaded && !error && arrangements.length === 0 && (
        <p className="text-[12.5px] leading-relaxed text-slate-600">
          No IP arrangement has been proposed on any of your projects. Nothing is
          waiting on you here — and a proposal would change nothing until you
          acknowledged it.
        </p>
      )}

      {loaded && !error && arrangements.map((a) => {
        const live = LIVE_STATUSES.has(a.status)
        const canAcknowledge = a.status === 'proposed' && a.my_state === 'pending'
        const note = statusSentence(a)
        return (
          <div
            key={a.arrangement_id}
            data-part="IpArrangementRow"
            className="border-t border-slate-100 py-2 first:border-t-0"
          >
            <p className="text-[12.5px] text-slate-600">
              <b className="text-slate-900">{a.project_name ?? 'A project of yours'}</b>
              {' · '}{kindLabel(a.kind)}{' · proposed by '}
              {a.proposed_by ?? 'an organisation'}
              {a.proposed_at ? ` ${dayAndMonth(a.proposed_at)}` : ''}
            </p>
            <p className="text-[11.5px] text-slate-500">
              {a.reference ? <>Reference: “{a.reference}” · </> : null}
              <b className="text-slate-700">{ownersSentence(a.owners_required)}</b>
            </p>
            {note && <p className="mt-1 text-[11.5px] text-slate-500">{note}</p>}

            {confirming === a.arrangement_id ? (
              <div className="mt-2 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2">
                <p className="text-[11.5px] leading-relaxed text-slate-700">
                  “{ACKNOWLEDGE_CONFIRMATION}”
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    disabled={busy === a.arrangement_id}
                    onClick={() => run(a.arrangement_id, onAcknowledge)}
                    className="rounded bg-iflow-600 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-iflow-700 disabled:opacity-60"
                  >
                    {busy === a.arrangement_id ? 'Recording…' : 'I confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(null)}
                    className="rounded border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : live ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {canAcknowledge && (
                  <button
                    type="button"
                    onClick={() => { setFailed(null); setConfirming(a.arrangement_id) }}
                    className="rounded bg-iflow-600 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-iflow-700"
                  >
                    Acknowledge
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy === a.arrangement_id}
                  onClick={() => run(a.arrangement_id, onDecline)}
                  className="rounded border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Decline
                </button>
                <button
                  type="button"
                  disabled={busy === a.arrangement_id}
                  onClick={() => run(a.arrangement_id, onDispute)}
                  className="rounded border border-slate-300 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Dispute
                </button>
              </div>
            ) : null}

            {failed === a.arrangement_id && (
              <p className="mt-1.5 text-[11.5px] text-rose-600">
                That did not go through, and nothing was recorded. Reload and try
                again — somebody else may have answered first.
              </p>
            )}
          </div>
        )
      })}

      <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
        Acknowledging asks you to confirm: “{ACKNOWLEDGE_CONFIRMATION}” It grants
        nothing — use the grid to decide what they see. Declining ends the
        proposal for every owner.
      </p>
    </div>
  )
}
